"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";

const SIZES = {
  sm: "max-w-[480px]",
  md: "max-w-[640px]",
  lg: "max-w-[960px]",
} as const;

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  size?: keyof typeof SIZES;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  dismissable?: boolean;
}

/**
 * Modal — Volume 03 §18. Một mục tiêu, Escape/click ngoài để đóng,
 * khóa scroll nền, initial focus vào panel.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  size = "md",
  children,
  footer,
  dismissable = true,
}: ModalProps) {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => setMounted(true), []);

  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panelRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && dismissable) onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, dismissable, onClose]);

  if (!open || !mounted) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={() => dismissable && onClose()}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn(
          "relative flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-2xl bg-surface shadow-overlay outline-none sm:rounded-2xl",
          SIZES[size]
        )}
      >
        {(title || dismissable) && (
          <div className="flex items-start justify-between gap-4 border-b border-border p-5">
            <div className="min-w-0">
              {title && <h2 className="text-h5 text-foreground">{title}</h2>}
              {description && <p className="mt-1 text-body-sm text-muted">{description}</p>}
            </div>
            {dismissable && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Đóng"
                className="-m-1 shrink-0 rounded-md p-1 text-muted hover:bg-subtle hover:text-foreground focus-visible:outline-none focus-visible:ring focus-visible:ring-ring"
              >
                <X className="size-5" aria-hidden="true" />
              </button>
            )}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto p-5">{children}</div>

        {footer && <div className="border-t border-border p-5">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}
